import { useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Loader2 } from 'lucide-react';
import { downloadLegalInvoicePdf } from '@/pages/billing/hooks/useLegalInvoices';
import { ccaaLabel } from './ccaa';
import { useLicenseOrderAction, useLicenseRecipes } from './hooks/useLicenseOrders';
import type { LicenseOrderAdjustment, LicenseOrderDetail } from './types';

interface Props {
  orderId: string;
  detail: LicenseOrderDetail;
  /** Abre el formulario y lo deja a la vista. Lo usa el menú de la tabla. */
  startOpen?: boolean;
}

type Quote = {
  fromRecipeName: string;
  toRecipeName: string;
  fromDurationLabel: string;
  toDurationLabel: string;
  fromOfficialCents: number;
  fromServiceCents: number;
  toOfficialCents: number;
  toServiceCents: number;
  chargeOfficialCents: number;
  chargeServiceCents: number;
  chargeCents: number;
  refundOfficialCents: number;
  refundServiceCents: number;
  refundCents: number;
  pending: { id: string; chargeCents: number } | null;
};

type ChangeResult = {
  status?: string;
  checkoutUrl?: string | null;
  emailed?: boolean;
  invoiceNumber?: string | null;
  refundError?: string | null;
};

const PAYMENT_LABEL: Record<string, string> = {
  stripe: 'Stripe',
  transfer: 'Transferencia',
  bizum: 'Bizum',
  manual: 'Manual',
};

function fmtEur(cents: number): string {
  return (
    ((cents || 0) / 100).toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + '\u00a0€'
  );
}

function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

function centsToInput(cents: number | null | undefined): string {
  const [whole, frac] = ((cents || 0) / 100).toFixed(2).split('.');
  return `${whole},${frac}`;
}

export function LicenseProductChangePanel({ orderId, detail, startOpen = false }: Props) {
  const recipesQuery = useLicenseRecipes();
  const action = useLicenseOrderAction(orderId);
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(startOpen);

  useEffect(() => {
    if (!startOpen) return;
    setOpen(true);
    rootRef.current?.scrollIntoView({ block: 'nearest' });
  }, [startOpen, orderId]);
  const [recipeId, setRecipeId] = useState('');
  const [duration, setDuration] = useState('');
  const [feeVariant, setFeeVariant] = useState('');
  const [manual, setManual] = useState(false);
  const [productLabel, setProductLabel] = useState('');
  const [durationLabel, setDurationLabel] = useState('');
  const [officialFee, setOfficialFee] = useState('');
  const [serviceFee, setServiceFee] = useState('');
  const [quote, setQuote] = useState<Quote | null>(null);
  const [quoteError, setQuoteError] = useState('');
  const [settlement, setSettlement] = useState<'stripe' | 'external'>('stripe');
  const [paymentSource, setPaymentSource] = useState('transfer');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentReceivedAt, setPaymentReceivedAt] = useState(todayIso);
  const [result, setResult] = useState<ChangeResult | null>(null);
  const [copied, setCopied] = useState(false);

  const recipes = useMemo(() => {
    const list = recipesQuery.data ?? [];
    return [...list]
      .filter((recipe) => recipe.status !== 'broken')
      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
  }, [recipesQuery.data]);

  const selected = recipes.find((recipe) => recipe.id === recipeId) ?? null;
  const durationOptions = selected?.durationOptions ?? [];
  const variants =
    durationOptions.find((option) => option.code === duration)?.feeVariants ?? [];
  const history = (detail.adjustments ?? []).filter((row) => row.status !== 'cancelled');

  function resetQuote() {
    setQuote(null);
    setQuoteError('');
    setResult(null);
  }

  function productFields(): Record<string, unknown> {
    if (manual) {
      return {
        manual: true,
        productLabel,
        durationLabel,
        officialFee,
        serviceFee,
      };
    }
    return {
      recipeId,
      duration,
      feeVariantCode: feeVariant || null,
    };
  }

  function enableManual(checked: boolean) {
    setManual(checked);
    resetQuote();
    if (checked) {
      setProductLabel('');
      setDurationLabel(detail.durationLabel || '');
      setOfficialFee(centsToInput(detail.officialFeeCents));
      setServiceFee(centsToInput(detail.serviceFeeCents));
    }
  }

  async function onQuote() {
    setQuoteError('');
    setResult(null);
    try {
      const data = (await action.mutateAsync({
        action: 'quote_product_change',
        ...productFields(),
      })) as unknown as Quote;
      setQuote(data);
    } catch (error) {
      setQuote(null);
      setQuoteError(error instanceof Error ? error.message : 'No se ha podido calcular la diferencia');
    }
  }

  async function onApply() {
    if (!quote) return;
    setQuoteError('');
    const body: Record<string, unknown> = {
      action: 'change_product',
      ...productFields(),
    };
    if (quote.chargeCents > 0) {
      body.settlement = settlement;
      if (settlement === 'external') {
        body.paymentSource = paymentSource;
        body.paymentReference = paymentReference;
        body.paymentReceivedAt = paymentReceivedAt;
      }
    }
    try {
      const data = (await action.mutateAsync(body)) as unknown as ChangeResult;
      setResult(data);
      if (data.status === 'applied') setQuote(null);
    } catch (error) {
      setQuoteError(error instanceof Error ? error.message : 'No se ha podido cambiar el producto');
    }
  }

  async function onRetryRefund(adjustmentId: string) {
    setQuoteError('');
    try {
      const data = (await action.mutateAsync({
        action: 'retry_adjustment_refund',
        adjustmentId,
      })) as unknown as ChangeResult;
      if (data.refundError) setQuoteError(data.refundError);
    } catch (error) {
      setQuoteError(error instanceof Error ? error.message : 'No se ha podido reintentar el reembolso');
    }
  }

  const canQuote = manual
    ? productLabel.trim().length >= 3 && officialFee.trim().length > 0 && serviceFee.trim().length > 0
    : Boolean(recipeId && duration);

  const applyLabel =
    !quote
      ? 'Cambiar producto'
      : quote.chargeCents > 0 && settlement === 'stripe'
        ? 'Enviar enlace de pago'
        : quote.chargeCents > 0
          ? 'Registrar cobro y cambiar'
          : quote.refundCents > 0
            ? 'Devolver diferencia y cambiar'
            : 'Cambiar producto';

  return (
    <div ref={rootRef} className="rounded-lg border border-border p-3 space-y-3">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="text-sm font-medium text-primary"
      >
        {open ? 'Cerrar cambio de producto' : 'Cambiar producto'}
      </button>

      {history.length > 0 && <AdjustmentList rows={history} onRetry={onRetryRefund} busy={action.isPending} />}

      {open && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={manual}
              onChange={(event) => enableManual(event.target.checked)}
            />
            No está en el catálogo: indicar nombre e importes a mano
          </label>
          {manual ? (
            <>
              <p className="text-[11px] text-muted-foreground">
                El pedido sigue asociado a la receta actual para la comunidad y la modalidad del trámite.
                El nombre y los importes que escribas son los que quedan en la ficha, en el listado y en la
                factura de la diferencia.
              </p>
              <label className="block">
                <span className="text-[11px] text-muted-foreground">Nombre del producto</span>
                <input
                  value={productLabel}
                  onChange={(event) => {
                    setProductLabel(event.target.value);
                    resetQuote();
                  }}
                  maxLength={120}
                  placeholder="Ej. Marítima desde tierra"
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-[11px] text-muted-foreground">Vigencia</span>
                <input
                  value={durationLabel}
                  onChange={(event) => {
                    setDurationLabel(event.target.value);
                    resetQuote();
                  }}
                  maxLength={80}
                  placeholder="Ej. 1 año"
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-[11px] text-muted-foreground">Tasa oficial del producto nuevo (€)</span>
                <input
                  value={officialFee}
                  onChange={(event) => {
                    setOfficialFee(event.target.value);
                    resetQuote();
                  }}
                  inputMode="decimal"
                  placeholder="12,26"
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-[11px] text-muted-foreground">Gestión AiPeces del producto nuevo (€)</span>
                <input
                  value={serviceFee}
                  onChange={(event) => {
                    setServiceFee(event.target.value);
                    resetQuote();
                  }}
                  inputMode="decimal"
                  placeholder="5,00"
                  className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
            </>
          ) : (
          <>
          <label className="block">
            <span className="text-[11px] text-muted-foreground">Producto nuevo</span>
            <select
              value={recipeId}
              onChange={(event) => {
                setRecipeId(event.target.value);
                setDuration('');
                setFeeVariant('');
                resetQuote();
              }}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">Elegir…</option>
              {recipes.map((recipe) => (
                <option key={recipe.id} value={recipe.id}>
                  {recipe.displayName} · {ccaaLabel(recipe.ccaa)}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[11px] text-muted-foreground">Vigencia</span>
            <select
              value={duration}
              onChange={(event) => {
                setDuration(event.target.value);
                setFeeVariant('');
                resetQuote();
              }}
              disabled={!selected}
              className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm disabled:opacity-50"
            >
              <option value="">Elegir…</option>
              {durationOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.label} · tasa {fmtEur(option.officialFeeCents)}
                </option>
              ))}
            </select>
          </label>
          {variants.length > 0 && (
            <label className="block">
              <span className="text-[11px] text-muted-foreground">Variante de tasa</span>
              <select
                value={feeVariant}
                onChange={(event) => {
                  setFeeVariant(event.target.value);
                  resetQuote();
                }}
                className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="">Tasa base</option>
                {variants.map((variant) => (
                  <option key={variant.code} value={variant.code}>
                    {variant.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          </>
          )}
          <button
            type="button"
            disabled={action.isPending || !canQuote}
            onClick={() => void onQuote()}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-border text-sm disabled:opacity-50"
          >
            {action.isPending && !quote ? <Loader2 size={14} className="animate-spin" /> : null}
            Calcular diferencia
          </button>

          {quote && (
            <div className="text-sm space-y-2 rounded-lg bg-muted/40 p-3">
              <p>
                {quote.fromRecipeName} ({quote.fromDurationLabel}) → {quote.toRecipeName} (
                {quote.toDurationLabel})
              </p>
              <p>
                Tasa {fmtEur(quote.fromOfficialCents)} → {fmtEur(quote.toOfficialCents)}. Gestión{' '}
                {fmtEur(quote.fromServiceCents)} → {fmtEur(quote.toServiceCents)}.
              </p>
              {quote.chargeCents > 0 && <p>A cobrar: {fmtEur(quote.chargeCents)}.</p>}
              {quote.refundCents > 0 && <p>A devolver: {fmtEur(quote.refundCents)}.</p>}
              {quote.chargeCents === 0 && quote.refundCents === 0 && (
                <p>El importe no cambia. Solo se actualiza el producto.</p>
              )}
              {quote.pending && (
                <p className="text-amber-700">
                  Hay un enlace pendiente de {fmtEur(quote.pending.chargeCents)}. Si continúas, se anula.
                </p>
              )}
              {quote.chargeCents > 0 && (
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="settlement"
                      checked={settlement === 'stripe'}
                      onChange={() => setSettlement('stripe')}
                    />
                    Enlace de Stripe por email
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="settlement"
                      checked={settlement === 'external'}
                      onChange={() => setSettlement('external')}
                    />
                    Ya cobrado fuera del Portal
                  </label>
                  {settlement === 'external' && (
                    <div className="space-y-2">
                      <select
                        value={paymentSource}
                        onChange={(event) => setPaymentSource(event.target.value)}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                      >
                        <option value="transfer">Transferencia</option>
                        <option value="bizum">Bizum</option>
                        <option value="manual">Manual</option>
                      </select>
                      <input
                        type="text"
                        value={paymentReference}
                        onChange={(event) => setPaymentReference(event.target.value)}
                        placeholder="Referencia del cobro"
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                      />
                      <input
                        type="date"
                        value={paymentReceivedAt}
                        onChange={(event) => setPaymentReceivedAt(event.target.value)}
                        className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                </div>
              )}
              <button
                type="button"
                disabled={action.isPending || (quote.chargeCents > 0 && settlement === 'external' && !paymentReceivedAt)}
                onClick={() => void onApply()}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm disabled:opacity-50"
              >
                {action.isPending ? <Loader2 size={14} className="animate-spin" /> : null}
                {applyLabel}
              </button>
            </div>
          )}

          {result?.checkoutUrl && (
            <div className="text-sm space-y-2">
              <p>
                {result.emailed
                  ? 'Enlace enviado al cliente. El producto no cambia hasta que pague.'
                  : 'El correo no ha salido. Copia el enlace y envíaselo.'}
              </p>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(result.checkoutUrl || '').then(() => {
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 2000);
                  });
                }}
                className="inline-flex items-center gap-2 text-primary"
              >
                <Copy size={14} />
                {copied ? 'Enlace copiado' : 'Copiar enlace de pago'}
              </button>
            </div>
          )}
          {result?.status === 'applied' && (
            <p className="text-sm text-emerald-600">
              Producto actualizado
              {result.invoiceNumber ? ` · factura ${result.invoiceNumber}` : ''}.
            </p>
          )}
          {result?.refundError && <p className="text-sm text-amber-700">{result.refundError}</p>}
          {quoteError && <p className="text-sm text-rose-500">{quoteError}</p>}
        </div>
      )}
    </div>
  );
}

function AdjustmentList({
  rows,
  onRetry,
  busy,
}: {
  rows: LicenseOrderAdjustment[];
  onRetry: (id: string) => void;
  busy: boolean;
}) {
  return (
    <ul className="space-y-2 text-sm">
      {rows.map((row) => (
        <li key={row.id} className="rounded-lg bg-muted/40 p-2">
          <div className="font-medium">
            {row.toRecipeName || 'Producto'} · {row.status === 'pending_payment' ? 'Pendiente de pago' : 'Aplicado'}
          </div>
          <div className="text-muted-foreground">
            {row.chargeCents > 0 ? `Cobro ${fmtEur(row.chargeCents)}` : 'Sin cobro'}
            {row.paymentSource ? ` · ${PAYMENT_LABEL[row.paymentSource] || row.paymentSource}` : ''}
            {row.legalInvoiceNumber ? ` · ${row.legalInvoiceNumber}` : ''}
            {row.creditNoteNumber ? ` · ${row.creditNoteNumber}` : ''}
          </div>
          {row.refundError && <div className="text-amber-700">{row.refundError}</div>}
          <div className="mt-1 flex flex-wrap gap-2">
            {row.legalInvoiceId && row.legalInvoiceNumber && (
              <button
                type="button"
                className="text-primary"
                onClick={() => {
                  void downloadLegalInvoicePdf(row.legalInvoiceId as string, row.legalInvoiceNumber as string);
                }}
              >
                Factura de la diferencia
              </button>
            )}
            {row.status === 'pending_payment' && row.checkoutUrl && (
              <button
                type="button"
                className="text-primary"
                onClick={() => void navigator.clipboard.writeText(row.checkoutUrl || '')}
              >
                Copiar enlace
              </button>
            )}
            {row.refundError && row.status === 'applied' && (
              <button type="button" disabled={busy} className="text-primary disabled:opacity-50" onClick={() => onRetry(row.id)}>
                Reintentar devolución
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
